// =============================================================================
// src/config/accessRules.js
// Same rules as the backend guard (middleware/smiifyAccessGuard.js), used by the
// browser to NOT send requests the user has no access to (no 403 in the console).
// Paths are relative to /api/v1/smiify/sm.
//
// need: 'module.action' or a list (any one is enough)
// 'SERVER' = depends on the request (bill status, discount...), the server decides
// null     = always allowed here (the page checks itself)
// Keep this file in step with the backend guard when you add routes.
// =============================================================================

export const SKIP_PREFIXES = ['/auth', '/settings', '/access', '/stores'];

const BILL_READERS = ['billing.view_history', 'billing.create', 'billing.view_bill', 'customers.view_history', 'dashboard.view'];
const CATALOG_READERS = ['pricelist.view', 'billing.create', 'appointments.view', 'memberships.sell_membership', 'packages.sell_package'];
const CLIENT_READERS = ['customers.view', 'billing.create', 'appointments.create', 'memberships.sell_membership'];
const STAFF_READERS = ['staff.view', 'billing.create', 'appointments.view', 'attendance.view', 'payroll.view', 'reports.view_staff_analytics'];

const R = (method, path, need) => ({ method, path, need });

export const ACCESS_RULES = [
    /* invoices */
    R('GET', /^\/invoices\/?$/, BILL_READERS),
    R('GET', /^\/invoices\/[^/]+$/, BILL_READERS),
    R('POST', /^\/invoices\/draft$/, 'billing.create'),
    R('PATCH', /^\/invoices\/[^/]+\/(save-draft|update)$/, 'SERVER'),
    R('PATCH', /^\/invoices\/[^/]+\/complete$/, ['billing.complete_bill', 'billing.create']),
    R('PATCH', /^\/invoices\/[^/]+\/cancel$/, 'SERVER'),
    R('PATCH', /^\/invoices\/[^/]+\/reschedule$/, 'billing.change_bill_date'),
    R('POST', /^\/invoices\/[^/]+\/(send|share-link)$/, ['billing.send_whatsapp', 'billing.send_whatsapp_api', 'billing.send_sms', 'billing.send_email', 'billing.download_bill', 'billing.print_bill']),

    /* customers */
    R('GET', /^\/customers(\/.*)?$/, CLIENT_READERS),
    R('POST', /^\/customers\/?$/, ['customers.create', 'billing.create', 'appointments.create']),
    R('POST', /^\/customers\/bulk-import$/, 'customers.import'),
    R('POST', /^\/customers\/allot-card$/, 'memberships.allot_cards'),
    R('POST', /^\/customers\/activate-discount-membership$/, ['memberships.allot_cards', 'memberships.sell_membership', 'billing.create']),
    R(['PUT', 'PATCH'], /^\/customers\/[^/]+\/wallet/, 'customers.manage_wallet'),
    R(['PUT', 'PATCH'], /^\/customers\/[^/]+\/merge/, 'customers.merge_profiles'),
    R(['PUT', 'PATCH', 'POST'], /^\/customers\/.+$/, ['customers.edit', 'billing.create']),
    R('DELETE', /^\/customers\/.+$/, 'customers.delete'),

    /* services */
    R('GET', /^\/services(\/.*)?$/, CATALOG_READERS),
    R('POST', /^\/services\/bulk$/, ['pricelist.import', 'pricelist.create', 'pricelist.edit']),   // each row is checked in the controller
    R('POST', /^\/services(\/.*)?$/, 'pricelist.create'),
    R(['PUT', 'PATCH'], /^\/services(\/.*)?$/, 'pricelist.edit'),
    R('DELETE', /^\/services(\/.*)?$/, 'pricelist.delete'),

    /* vendors */
    R('GET', /^\/vendors(\/.*)?$/, 'vendors.view'),
    R('POST', /^\/vendors\/?$/, 'vendors.manage'),
    R('PUT', /^\/vendors\/[^/]+$/, 'vendors.manage'),
    R('POST', /^\/vendors\/bills$/, 'vendors.record_purchase'),
    R('PATCH', /^\/vendors\/bills\/[^/]+\/cancel$/, 'vendors.void'),
    R('POST', /^\/vendors\/payments$/, 'vendors.record_payment'),
    R('PATCH', /^\/vendors\/payments\/[^/]+\/clear$/, 'vendors.clear_payment'),
    R('PATCH', /^\/vendors\/payments\/[^/]+\/void$/, 'vendors.void'),
    R('POST', /^\/vendors\/returns$/, 'vendors.record_return'),

    /* inventory */
    R('GET', /^\/inventory\/analytics$/, ['inventory.view_analytics', 'reports.view_inventory_analytics']),
    R('GET', /^\/inventory\/(movements|[^/]+\/movements)$/, ['inventory.view_movements', 'inventory.view']),
    R('GET', /^\/inventory\/?$/, ['inventory.view', 'billing.create']),
    R('POST', /^\/inventory\/bulk-add$/, 'inventory.create'),
    R('POST', /^\/inventory\/movement$/, 'inventory.adjust_stock'),
    R('PUT', /^\/inventory\/bulk-update$/, 'inventory.bulk_update'),
    R('PUT', /^\/inventory\/deactivate$/, 'inventory.delete'),
    R('PUT', /^\/inventory\/[^/]+$/, 'inventory.edit'),

    /* appointments */
    R('GET', /^\/appointments(\/.*)?$/, ['appointments.view', 'dashboard.view_appointment_metrics', 'dashboard.view_live_board']),
    R('POST', /^\/appointments\/[^/]+\/notifications$/, ['appointments.send_whatsapp', 'appointments.send_staff_schedule', 'dashboard.send_whatsapp']),
    R('PATCH', /^\/appointments\/[^/]+\/status$/, 'SERVER'),
    R('POST', /^\/appointments\/?$/, 'appointments.create'),
    R(['PUT', 'PATCH'], /^\/appointments\/[^/]+$/, ['appointments.edit', 'appointments.reschedule']),
    R('DELETE', /^\/appointments\/[^/]+$/, 'appointments.delete'),

    /* staff */
    R(['GET', 'PUT'], /^\/staff\/[^/]+\/policy$/, null),
    R('GET', /^\/staff(\/[^/]+)?$/, STAFF_READERS),
    R('POST', /^\/staff\/?$/, 'staff.create'),
    R('POST', /^\/staff\/[^/]+\/credentials$/, 'users.create'),
    R('PATCH', /^\/staff\/[^/]+\/deactivate$/, ['staff.delete', 'users.delete']),
    R('PATCH', /^\/staff\/[^/]+\/reactivate$/, 'staff.reactivate'),
    R('PUT', /^\/staff\/[^/]+$/, 'SERVER'),

    /* attendance */
    R('GET', /^\/attendance(\/.*)?$/, 'attendance.view'),
    R('POST', /^\/attendance\/punch$/, ['attendance.punch_self', 'attendance.punch_others']),
    R('POST', /^\/attendance\/staff\/[^/]+\/month\/[^/]+\/overtime$/, 'attendance.approve_overtime'),
    R('POST', /^\/attendance\/staff\/[^/]+\/[^/]+\/punches$/, 'attendance.edit_entries'),
    R(['PUT', 'DELETE'], /^\/attendance\/staff\/[^/]+\/[^/]+\/punches\/[^/]+$/, 'attendance.edit_entries'),
    R('PUT', /^\/attendance\/staff\/[^/]+\/[^/]+\/status$/, ['attendance.mark_leave', 'attendance.approve_overtime']),

    /* payroll */
    R('GET', /^\/payroll\/incentives$/, ['payroll.view_incentives', 'payroll.view']),
    R('GET', /^\/payroll(\/.*)?$/, 'payroll.view'),
    R('POST', /^\/payroll\/runs\/[^/]+\/generate$/, 'payroll.create'),
    R('PUT', /^\/payroll\/runs\/[^/]+\/lines\/[^/]+$/, 'payroll.edit'),
    R('POST', /^\/payroll\/runs\/[^/]+\/finalize$/, 'payroll.process_payout'),
    R('POST', /^\/payroll\/runs\/[^/]+\/reopen$/, 'payroll.reopen'),
    R('POST', /^\/payroll\/runs\/[^/]+\/pay$/, 'payroll.mark_paid'),

    /* expenses */
    R('GET', /^\/expenses(\/.*)?$/, 'expense.view'),
    R('PATCH', /^\/expenses\/[^/]+\/status$/, 'expense.approve'),
    R('POST', /^\/expenses\/?$/, 'SERVER'),
    R(['PUT', 'PATCH'], /^\/expenses\/[^/]+$/, 'expense.edit'),
    R('DELETE', /^\/expenses\/[^/]+$/, 'expense.delete'),

    /* memberships & packages */
    R('GET', /^\/memberships\/customers\/[^/]+\/wallet$/, ['billing.create', 'memberships.view', 'customers.view']),
    R('GET', /^\/memberships\/history$/, ['memberships.view_passbook', 'memberships.view']),
    R('GET', /^\/memberships(\/.*)?$/, ['memberships.view', 'billing.create']),
    R('POST', /^\/memberships\/quote$/, ['memberships.sell_membership', 'memberships.renew', 'billing.create']),
    R('POST', /^\/memberships\/checkout$/, ['memberships.sell_membership', 'memberships.renew', 'billing.create']),

    R('GET', /^\/prepaid-memberships\/cards\/available\/[^/]+$/, ['memberships.allot_cards', 'memberships.view_prepaid']),
    R('GET', /^\/prepaid-memberships(\/.*)?$/, ['memberships.view_prepaid', 'memberships.view', 'billing.create']),
    R('POST', /^\/prepaid-memberships\/allot-card$/, 'memberships.allot_cards'),
    R('POST', /^\/prepaid-memberships\/generate-batch$/, ['memberships.create_prepaid', 'memberships.edit_prepaid']),
    R('POST', /^\/prepaid-memberships(\/.*)?$/, 'memberships.create_prepaid'),
    R('PATCH', /^\/prepaid-memberships\/(toggle-status|[^/]+\/status)$/, 'memberships.deactivate_prepaid'),
    R(['PUT', 'PATCH'], /^\/prepaid-memberships(\/.*)?$/, 'memberships.edit_prepaid'),
    R('DELETE', /^\/prepaid-memberships(\/.*)?$/, 'memberships.deactivate_prepaid'),

    R('GET', /^\/discount-memberships(\/.*)?$/, ['memberships.view_discount', 'memberships.view', 'billing.create']),
    R('POST', /^\/discount-memberships\/activate-discount-membership$/, ['memberships.allot_cards', 'memberships.sell_membership', 'billing.create']),
    R('POST', /^\/discount-memberships\/save$/, ['memberships.create_discount', 'memberships.edit_discount']),
    R('POST', /^\/discount-memberships(\/.*)?$/, 'memberships.create_discount'),
    R('PATCH', /^\/discount-memberships\/(toggle-status|[^/]+\/status)$/, 'memberships.deactivate_discount'),
    R(['PUT', 'PATCH'], /^\/discount-memberships(\/.*)?$/, 'memberships.edit_discount'),
    R('DELETE', /^\/discount-memberships(\/.*)?$/, 'memberships.deactivate_discount'),

    R('GET', /^\/packages(\/.*)?$/, ['packages.view', 'billing.create']),
    R('POST', /^\/packages(\/.*)?$/, 'packages.create'),
    R('PATCH', /^\/packages\/(toggle-status|[^/]+\/status)$/, 'packages.deactivate'),
    R(['PUT', 'PATCH'], /^\/packages(\/.*)?$/, 'packages.edit'),
    R('DELETE', /^\/packages(\/.*)?$/, 'packages.deactivate'),

    /* daily closing report + cash drawer */
    R('GET', /^\/daily-report\/?$/, ['reports.view_daily_report', 'reports.view_eod_report']),
    R('POST', /^\/daily-report\/share$/, ['reports.share_daily_report', 'reports.view_daily_report', 'reports.view_eod_report', 'cashflow.close_day']),
    R('GET', /^\/cash-flow\/?$/, 'cashflow.view'),
    R('GET', /^\/cash-flow\/range$/, 'cashflow.view'),
    R('POST', /^\/cash-flow\/entries$/, ['cashflow.add_cash_in', 'cashflow.add_cash_out']),
    R('PATCH', /^\/cash-flow\/entries\/[^/]+\/void$/, 'cashflow.void_entry'),
    R('POST', /^\/cash-flow\/close$/, 'cashflow.close_day'),
    R('DELETE', /^\/cash-flow\/close$/, 'cashflow.reopen_day'),

    /* analytics & reports */
    R('GET', /^\/analytics(\/.*)?$/, ['reports.view', 'dashboard.view_financial_metrics', 'dashboard.view_staff_metrics', 'dashboard.view']),
    R('GET', /^\/reports(\/.*)?$/, 'reports.view'),

    /* marketing */
    R('GET', /^\/(campaigns|templates)(\/.*)?$/, 'marketing.view'),
    R('POST', /^\/campaigns\/launch$/, 'marketing.send_campaign'),
    R('POST', /^\/campaigns\/[^/]+\/cancel$/, 'marketing.send_campaign'),
    R(['POST', 'DELETE'], /^\/campaigns\/optouts(\/.*)?$/, 'marketing.edit_settings'),
    R('POST', /^\/campaigns\/[^/]+\/send/, 'marketing.send_campaign'),
    R(['POST', 'PUT', 'PATCH', 'DELETE'], /^\/campaigns(\/.*)?$/, 'marketing.create_campaign'),
    R(['POST', 'PUT', 'PATCH', 'DELETE'], /^\/templates(\/.*)?$/, 'marketing.manage_templates'),

    /* WhatsApp integration */
    R('POST', /^\/integrations\/whatsapp\/(send|message|messages|invoice|test-message)(\/.*)?$/, ['billing.send_whatsapp_api', 'billing.send_whatsapp', 'appointments.send_whatsapp', 'dashboard.send_whatsapp', 'marketing.send_campaign']),
    R('GET', /^\/integrations\/whatsapp\/line$/, ['marketing.view', 'integrations.view', 'wallet.view']),
    R('GET', /^\/integrations\/whatsapp(\/.*)?$/, ['integrations.view', 'billing.send_whatsapp', 'appointments.send_whatsapp']),
    R(['POST', 'PUT', 'PATCH', 'DELETE'], /^\/integrations\/whatsapp(\/.*)?$/, 'integrations.edit_whatsapp'),
    R('GET', /^\/integrations\/sms(\/.*)?$/, 'integrations.view'),
    R(['POST', 'PUT', 'PATCH', 'DELETE'], /^\/integrations\/sms(\/.*)?$/, 'integrations.edit_sms'),
    R('GET', /^\/integrations\/email(\/.*)?$/, 'integrations.view'),
    R(['POST', 'PUT', 'PATCH', 'DELETE'], /^\/integrations\/email(\/.*)?$/, 'integrations.edit_email'),

    /* branch logo */
    R(['POST', 'DELETE'], /^\/branding\/logo$/, ['settings.edit_profile', 'settings.edit']),

    /* store wallet */
    R('GET', /^\/wallet(\/.*)?$/, 'wallet.view'),
    R('POST', /^\/wallet\/transfer$/, 'wallet.transfer'),
    R(['POST', 'PUT', 'PATCH'], /^\/wallet(\/.*)?$/, 'wallet.recharge'),
];

export const findAccessRule = (method, path) => {
    const m = String(method || 'GET').toUpperCase();
    for (const rule of ACCESS_RULES) {
        const methods = Array.isArray(rule.method) ? rule.method : [rule.method];
        if (methods.includes(m) && rule.path.test(path)) return rule;
    }
    return null;
};
